import { QueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

// Create QueryClient with configurations that align with secureAxios
// secureAxios uses default axios timeout (0 = no timeout) and no retry logic
export const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            // Time in milliseconds that unused/inactive cache data remains in memory
            cacheTime: 1000 * 60 * 5, // 5 minutes
            // Time in milliseconds after data is considered stale
            staleTime: 1000 * 60 * 1, // 1 minute
            // Retry failed requests 3 times with exponential backoff
            // Since secureAxios has no timeout, we handle all network errors here
            retry: (failureCount, error: unknown) => {
                // Use axios error checking for precise error handling
                if (isAxiosError(error)) {
                    // Don't retry on 4xx errors (client errors)
                    if (error.response?.status && error.response.status >= 400 && error.response.status < 500) {
                        return false;
                    }
                    // Since secureAxios has no timeout, axios won't generate ECONNABORTED for timeouts
                    // But we still handle any potential timeout-like errors
                    if (error.code === 'ECONNABORTED') {
                        return false;
                    }
                }
                return failureCount < 3;
            },
            // Retry delay with exponential backoff
            // Since secureAxios has no timeout, requests might take longer
            // Keep max delay reasonable to avoid extremely long retry cycles
            retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
            // Refetch on window focus only if data is stale
            refetchOnWindowFocus: 'always',
            // Refetch on network reconnect
            refetchOnReconnect: 'always',
            // Don't refetch on mount if data is fresh
            refetchOnMount: true
        },
        mutations: {
            // Retry failed mutations 1 time (less aggressive than queries)
            // Since secureAxios has no timeout, be more conservative with mutations
            retry: (failureCount, error: unknown) => {
                // For mutations, be conservative with retries
                if (isAxiosError(error)) {
                    // Never retry 4xx errors for mutations
                    if (error.response?.status && error.response.status >= 400 && error.response.status < 500) {
                        return false;
                    }
                    // Never retry any connection errors for mutations
                    if (error.code === 'ECONNABORTED' || error.code === 'ENOTFOUND' || error.code === 'ECONNREFUSED') {
                        return false;
                    }
                }
                return failureCount < 1;
            },
            // Retry delay for mutations (shorter than queries)
            retryDelay: 1000
        }
    }
});
